/**
 * Jeu d'icônes (US-54).
 *
 * ### Pourquoi il n'y en avait pas
 *
 * Le dépôt comptait huit `<svg>` à usage unique — une coche, un chevron, une
 * assiette — et **une trentaine de caractères Unicode** tenant lieu d'icônes :
 * `✕` pour fermer, `★` pour la note, `−` pour décrémenter, `○` pour un état.
 * Trois conséquences, toutes visibles :
 *
 * - **le dessin dépend de la police installée.** `ui/Checkbox.tsx` le disait
 *   déjà en dessinant sa coche en SVG « parce qu'une police peut ne pas
 *   l'avoir » — mais six autres endroits écrivaient le caractère `✓` ;
 * - **la taille et l'alignement dépendent de la fonte**, donc varient d'un
 *   écran à l'autre pour le même geste. Le `✕` de fermeture recevait trois
 *   traitements différents sur le seul écran d'accueil ;
 * - **aucune couleur ni épaisseur commune** : un glyphe est du texte, il hérite
 *   de tout et ne se règle nulle part.
 *
 * ### Les règles
 *
 * Une icône est **toujours décorative** (`aria-hidden`) : le sens vient du
 * libellé du contrôle ou de son `aria-label`, jamais du dessin. Elle hérite de
 * la couleur du texte (`currentColor`) et se dimensionne en `em`, donc suit la
 * taille de police autour d'elle — y compris le réglage 20→32 px du mode
 * cuisine, sans rien à recâbler.
 *
 * Le trait est en `stroke`, jamais en `fill`, sauf pour l'étoile : une note se
 * lit d'un coup d'œil, un contour ne se lit pas.
 */

export type IconName =
  | 'croix'
  | 'coche'
  | 'etoile'
  | 'moins'
  | 'plus'
  | 'cercle'
  | 'alerte'
  // Navigation (US-56). Une par destination, plus le geste d'ajout.
  | 'livre'
  | 'calendrier'
  | 'panier'
  | 'carnet'
  | 'personne'
  | 'filtre'
  | 'casserole'
  // Commande vocale (US-59). Une par mot reconnu, plus le micro lui-même :
  // « suivant », « précédent », « minuteur » n'étaient donnés qu'en toutes
  // lettres, et rien ne reliait le mot au geste qu'il déclenche.
  | 'fleche-gauche'
  | 'fleche-droite'
  // Ordre des étapes (US-77). Les commandes « monter »/« descendre » étaient
  // écrites `↑` et `↓` — deux caractères Unicode en guise d'icônes, ce que §4
  // proscrit depuis US-54 et que la story qui les a posées n'a pas vu.
  | 'fleche-haut'
  | 'fleche-bas'
  | 'minuteur'
  | 'micro'
  // Réactions à une réalisation (US-66). Liste fermée de quatre, dessinées :
  // un emoji change de dessin d'un système à l'autre, et §6 les proscrit.
  | 'bravo'
  | 'envie'
  | 'question'
  | 'refaire'
  // Suppression définitive (US-83). Elle ne vit **que** sur l'écran des
  // recettes archivées : c'est la seule commande de l'application qui perde
  // une donnée, et la seule qui ait besoin d'un dessin reconnaissable au coin
  // d'une carte, là où le raccourci de cuisson n'a pas lieu d'être.
  | 'corbeille';

export interface IconProps {
  name: IconName;
  /**
   * Multiplicateur de la taille de police courante. 1 = hauteur d'une
   * majuscule environ. Ignoré si `sizeClassName` est fourni.
   */
  size?: number;
  /**
   * Classes de largeur/hauteur qui remplacent `size` — pour les rares cas où
   * la taille change selon le point de rupture (`lg:` etc.), qu'un style en
   * ligne ne sait pas exprimer (US-89). Les deux mécanismes ne coexistent
   * jamais sur un même élément : un style en ligne gagne toujours contre une
   * classe, quel que soit l'ordre du CSS compilé — silencieusement, ce qui a
   * produit le doublon d'icône de la navigation (F-01).
   */
  sizeClassName?: string;
  className?: string;
}

const CHEMINS: Record<IconName, { d: string; fill?: boolean }> = {
  croix: { d: 'M5 5 L15 15 M15 5 L5 15' },
  coche: { d: 'M4 10.5 L8 14.5 L16 5.5' },
  etoile: { d: 'M10 2.5 L12.35 7.6 L17.9 8.3 L13.8 12.1 L14.9 17.6 L10 14.9 L5.1 17.6 L6.2 12.1 L2.1 8.3 L7.65 7.6 Z', fill: true },
  moins: { d: 'M4.5 10 H15.5' },
  plus: { d: 'M4.5 10 H15.5 M10 4.5 V15.5' },
  cercle: { d: 'M10 3.5 A6.5 6.5 0 1 1 9.99 3.5' },
  alerte: { d: 'M10 4 V11.5 M10 14.5 V15.5' },
  // Un livre ouvert : la bibliothèque.
  livre: { d: 'M10 5.5 C8 4 5.5 3.8 3.5 4.3 V15.2 C5.5 14.7 8 14.9 10 16.4 C12 14.9 14.5 14.7 16.5 15.2 V4.3 C14.5 3.8 12 4 10 5.5 Z M10 5.5 V16.4' },
  calendrier: { d: 'M4 5.5 H16 V16 H4 Z M4 8.5 H16 M7.5 3.5 V6 M12.5 3.5 V6' },
  panier: { d: 'M3.5 6 H16.5 L15 15.5 H5 Z M7 9 V12.5 M13 9 V12.5 M10 9 V12.5' },
  // Un carnet : le journal des réalisations.
  carnet: { d: 'M5.5 3.5 H15.5 V16.5 H5.5 Z M5.5 3.5 A1.6 1.6 0 0 0 5.5 6.7 M8.5 8 H13 M8.5 11 H13' },
  personne: { d: 'M10 4 A2.6 2.6 0 1 1 9.99 4 M4.8 16.3 C4.8 13.4 7.1 11.6 10 11.6 C12.9 11.6 15.2 13.4 15.2 16.3' },
  filtre: { d: 'M3.5 5 H16.5 L11.6 10.6 V15.8 L8.4 17 V10.6 Z' },
  // Une casserole vue de côté : cuisiner.
  casserole: { d: 'M3.5 8.5 H14.5 V12 A3 3 0 0 1 11.5 15 H6.5 A3 3 0 0 1 3.5 12 Z M14.5 9.8 H17 M6 8.5 V6.2' },
  'fleche-gauche': { d: 'M15.5 10 H4.5 M9 4.5 L4 10 L9 15.5' },
  'fleche-droite': { d: 'M4.5 10 H15.5 M11 4.5 L16 10 L11 15.5' },
  'fleche-haut': { d: 'M10 15.5 V4.5 M4.5 10 L10 4.5 L15.5 10' },
  'fleche-bas': { d: 'M10 4.5 V15.5 M4.5 10 L10 15.5 L15.5 10' },
  // Un cadran de minuteur de cuisine : cercle, aiguilles, bouton sur le dessus.
  minuteur: { d: 'M10 5.5 A6 6 0 1 1 9.99 5.5 M10 7.8 V11 H12.6 M8.4 3 H11.6' },
  // Deux mains qui applaudissent, stylisées : deux arcs qui se rejoignent.
  bravo: { d: 'M7 15.5 C4.5 13 4.5 9 7 6.5 M13 15.5 C15.5 13 15.5 9 13 6.5 M9.2 13.5 C8 12 8 9.5 9.2 8 M10.8 13.5 C12 12 12 9.5 10.8 8' },
  // Une fourchette et un couteau : « ça donne envie ».
  envie: { d: 'M7 3.5 V9 M5.5 3.5 V6.5 M8.5 3.5 V6.5 M7 9 V16.5 M13.5 3.5 C15 5 15 8 13.5 9.5 V16.5' },
  question: { d: 'M7.6 7.4 A2.5 2.5 0 1 1 10 11 V12.3 M10 15 V15.9' },
  // Une flèche qui boucle : refaire.
  refaire: { d: 'M15.5 10 A5.5 5.5 0 1 1 13.6 5.8 M13.6 5.8 H16.2 M13.6 5.8 V3.2' },
  corbeille: { d: 'M4 6 H16 M8 6 V4.2 H12 V6 M5.8 6 L6.6 16.2 H13.4 L14.2 6 M8.6 8.8 V13.4 M11.4 8.8 V13.4' },
  micro: { d: 'M10 3.2 A2 2 0 0 1 12 5.2 V9.2 A2 2 0 0 1 8 9.2 V5.2 A2 2 0 0 1 10 3.2 Z M5.5 9.2 A4.5 4.5 0 0 0 14.5 9.2 M10 13.7 V16.8' },
};

export function Icon({ name, size = 1, sizeClassName, className = '' }: IconProps): React.JSX.Element {
  const forme = CHEMINS[name];
  return (
    <svg
      viewBox="0 0 20 20"
      // `em` et non `px` : l'icône suit la taille du texte autour d'elle, donc
      // le réglage 20→32 px du mode cuisine, sans réglage supplémentaire.
      // Absent quand `sizeClassName` est fourni : les deux ne doivent jamais
      // se disputer un même axe, un style en ligne battant toujours une classe.
      style={sizeClassName === undefined ? { width: `${String(size)}em`, height: `${String(size)}em` } : undefined}
      className={`inline-block shrink-0 align-[-0.125em] ${sizeClassName ?? ''} ${className}`.trim()}
      // Toujours décorative : le sens vient du libellé ou de l'`aria-label` du
      // contrôle qui la porte. Une icône qui porterait seule le sens serait
      // muette pour un lecteur d'écran.
      aria-hidden="true"
      focusable="false"
    >
      <path
        d={forme.d}
        fill={forme.fill === true ? 'currentColor' : 'none'}
        stroke="currentColor"
        strokeWidth={forme.fill === true ? 0 : 2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
